import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// NOTE: do NOT enable withCredentials — the platform ingress sends
// Access-Control-Allow-Origin: * which is incompatible with credentials.
// JWT is delivered in the response body and persisted in localStorage as
// a Bearer token (see interceptor below).
const client = axios.create({ baseURL: API, timeout: 30000 });

const TOKEN_KEY = "pmv5_csuite_token";
const getToken = () => { try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; } };
const setToken = (t) => { try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY); } catch (e) { /* ignore */ } };

client.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

function err(e) {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join(" ");
  if (typeof d === "string") return d;
  return e?.message || "Request failed";
}

export const csuiteApi = {
  signup:      (payload) => client.post("/csuite/signup", payload).then(r => r.data).catch(e => Promise.reject(new Error(err(e)))),
  requestCode: (email)   => client.post("/csuite/request-code", { email }).then(r => r.data).catch(e => Promise.reject(new Error(err(e)))),
  verifyCode:  (email, code) => client.post("/csuite/verify-code", { email, code }).then(r => {
    if (r.data.token) setToken(r.data.token);
    return r.data;
  }).catch(e => Promise.reject(new Error(err(e)))),
  login:       (email, password) => client.post("/csuite/login", { email, password }).then(r => {
    if (r.data.token) setToken(r.data.token);
    return r.data;
  }).catch(e => Promise.reject(new Error(err(e)))),
  logout:      () => client.post("/csuite/logout").then(r => { setToken(""); return r.data; }).catch(e => { setToken(""); return Promise.reject(new Error(err(e))); }),
  me:          () => client.get("/csuite/me").then(r => r.data).catch(e => Promise.reject(new Error(err(e)))),
  rollup:      () => client.get("/csuite/rollup").then(r => r.data).catch(e => Promise.reject(new Error(err(e)))),
  // ─── Platinum endpoints (require active paid subscription) ───────────
  dynamics365: () => client.get("/platinum/dynamics365").then(r => r.data).catch(e => Promise.reject(new Error(err(e)))),
  scorecard:   () => client.get("/platinum/scorecard").then(r => r.data).catch(e => Promise.reject(new Error(err(e)))),
  value:       () => client.get("/platinum/value").then(r => r.data).catch(e => Promise.reject(new Error(err(e)))),
  insights:    () => client.get("/platinum/insights").then(r => r.data).catch(e => Promise.reject(new Error(err(e)))),
  hana:        () => client.get("/platinum/hana").then(r => r.data).catch(e => Promise.reject(new Error(err(e)))),
  aiChat:      (session_id, message, action) =>
                client.post("/platinum/ai/chat", { session_id, message, action }, { timeout: 60000 })
                      .then(r => r.data).catch(e => Promise.reject(new Error(err(e)))),
  aiHistory:   (session_id) => client.get(`/platinum/ai/history/${session_id}`).then(r => r.data).catch(e => Promise.reject(new Error(err(e)))),
  aiAlertsUnread: () => client.get("/platinum/ai/alerts/unread").then(r => r.data).catch(e => Promise.reject(new Error(err(e)))),
  aiAlertsMarkRead: () => client.post("/platinum/ai/alerts/mark-read").then(r => r.data).catch(e => Promise.reject(new Error(err(e)))),
  // ─── Live-tenant connections ────────────────────────────────────────
  connections:      () => client.get("/platinum/connections").then(r => r.data).catch(e => Promise.reject(new Error(err(e)))),
  saveD365Config:   (cfg) => client.post("/platinum/connections/d365", cfg).then(r => r.data).catch(e => Promise.reject(new Error(err(e)))),
  saveHanaConfig:   (cfg) => client.post("/platinum/connections/hana", cfg).then(r => r.data).catch(e => Promise.reject(new Error(err(e)))),
  deleteConnection: (kind) => client.delete(`/platinum/connections/${kind}`).then(r => r.data).catch(e => Promise.reject(new Error(err(e)))),
  testD365:         (cfg) => client.post("/platinum/connections/test/d365", cfg, { timeout: 30000 }).then(r => r.data).catch(e => Promise.reject(new Error(err(e)))),
  testHana:         (cfg) => client.post("/platinum/connections/test/hana", cfg, { timeout: 30000 }).then(r => r.data).catch(e => Promise.reject(new Error(err(e)))),
  // ─── Referral rewards ───────────────────────────────────────────────
  referralsMe: () => client.get("/referrals/me").then(r => r.data).catch(e => Promise.reject(new Error(err(e)))),
};

export const csuiteAuth = { getToken, setToken };
